import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import {
  Step2CTA1, Step2LearnMore, TermsAndConditionsText, Step2BoxRow, Step2EnrollmentErrorMessage,
} from './tenantComponents';
import usePostEnrollment from '../../apiHooks/usePostEnrollment';
import { getMicrositeTenantAssetsDir, getTenantAssetsDir } from '../../utils';


/**
 *
 * @param {object} props
 * @param {(response: EHersEnrollResponse) => void} props.onSuccess
 * @param {string} props.encAccountId
 * @param {string} props.encServiceLocationId
 * @param {string} props.displayAccountId
 * @param {string} props.streetAddress
 * @param {string} props.lastName
 * @param {string} props.zipCode
 * @returns {JSX.Element}
 */
// eslint-disable-next-line max-lines-per-function
const EHersEnrollmentStep2 = ({
  onSuccess, encAccountId, encServiceLocationId, encExternalAccountId,
  displayAccountId,
  streetAddress,
  lastName,
  zipCode,
  email,
}) => {
  const sanitizeEmailInput = (value) => value.replace(/[^A-Za-z0-9@.+_-]/g, '');

  const [alertIsClosed, setAlertIsClosed] = useState(false);
  const onCloseAlert = () => {
    setAlertIsClosed(true);
  };

  const currentYear = new Date().getFullYear();

  const {
    hostId,
    logoHostAssetName,
  } = window.SE_CLIENT_CONFIG;

  const hostDomain = window.SE_HOST_DOMAIN;
  const micrositeId = window.SE_MICROSITE_ID;
  const assetsHostsDir = getTenantAssetsDir(hostDomain, hostId);
  const assetsMicrositeDir = getMicrositeTenantAssetsDir(hostDomain, micrositeId, hostId);

  // on mount, scroll to top
  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);


  const {
    // isError: checkEligibilityError,
    mutate: postEnrollmentMutate,
    isLoading: postEnrollmentLoading,
    isError: postEnrollmentIsError,
  } = usePostEnrollment(hostDomain);


  const onClickGetStarted = (evt) => {
    const form = document.getElementById('offer-form');

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    evt.preventDefault();
    setAlertIsClosed(false);

    const enteredEmail = (document.getElementById('email')).value;

    postEnrollmentMutate(
      {
        encAccountId,
        encServiceLocationId,
        encExternalAccountId,
        email: enteredEmail,
        zipCode,
      },
      {
        onSuccess: (enrollmentResponse) => {
          // console.log('Enrollment successful:', enrollmentResponse);
          // alert('Enrollment successful!');
          onSuccess(enrollmentResponse);
        },
        onError: () => {
          // console.error('Enrollment failed:', error);
          // alert('Enrollment failed. Please try again.');
        },
      },
    );
  };

  return (
    <div>
      <div id="offer-page" className="container">
        <div className="row">
          <div className="col-lg-10 col-lg-offset-1 col-md-10 col-md-offset-1 col-sm-12 col-xs-12">
            <div className="top-logo">
              <img src={`${assetsHostsDir}/${logoHostAssetName}`} alt="logo" className="top-logo-img" />
            </div>
            <div className="row info-row">
              <div id="content-container" className="container-fluid">
                <div className="row">
                  <div id="left-container" className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
                    <div className="left-container-copy">
                      <h1 className="tag-line">Home Energy Reports</h1>
                      <Step2CTA1 hostId={hostId} />
                    </div>
                    <div id="offer-container">
                      <p className="name up-truncate">{lastName?.toUpperCase()}</p>
                      <p className="address up-truncate">{streetAddress?.toUpperCase()}</p>
                      <p className="zip-code">{zipCode}</p>
                      <p id="confirm-text">
                        Please enter the email address you would like us to send the monthly home
                        energy report for account &#39;
                        {displayAccountId}
                        &#39;:
                      </p>
                      <form method="POST" id="offer-form">
                        <div className="row">
                          <div className="col-xs-12">
                            <div className="form-group ">
                              <input
                                id="email"
                                type="email"
                                data-testid="email"
                                className="form-control input-sm"
                                name="email"
                                defaultValue={email}
                                required
                                pattern="^[A-Za-z0-9@.+_-]+$"
                                title="Only letters, numbers, and email characters (@ . + _ -) are allowed"
                                onInput={(evt) => {
                                  evt.currentTarget.value = sanitizeEmailInput(evt.currentTarget.value);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <Collapse
                          style={{ marginBottom: postEnrollmentIsError && !alertIsClosed ? 25 : 0 }}
                          in={postEnrollmentIsError && !alertIsClosed}
                        >
                          <Step2EnrollmentErrorMessage hostId={hostId} onClose={onCloseAlert} />
                        </Collapse>
                        <div className="form-group">
                          <button disabled={postEnrollmentLoading} type="submit" className="btn btn-block btn-green" onClick={onClickGetStarted} id="confirm-and-enroll-button-small">Confirm and Enroll</button>
                          <button disabled={postEnrollmentLoading} type="submit" className="btn btn-block btn-green" onClick={onClickGetStarted} id="confirm-and-enroll-button-big">Confirm Information and Enroll</button>
                        </div>
                      </form>
                    </div>
                  </div>
                  <Step2LearnMore hostId={hostId} />
                </div>
              </div>
            </div>
            <div className="row" />
            <Step2BoxRow hostId={hostId} assetsMicrositeDir={assetsMicrositeDir} />
            <div className="row footer-row">
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <div id="footer-container" className="text-center">
                  <TermsAndConditionsText hostId={hostId} currentYear={currentYear} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EHersEnrollmentStep2.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  encAccountId: PropTypes.string.isRequired,
  encServiceLocationId: PropTypes.string.isRequired,
  encExternalAccountId: PropTypes.string.isRequired,
  displayAccountId: PropTypes.string.isRequired,
  streetAddress: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  zipCode: PropTypes.string.isRequired,
  email: PropTypes.string,
};

EHersEnrollmentStep2.defaultProps = {
  email: '',
};

export default EHersEnrollmentStep2;
